/**
 * SSoT (Single Source of Truth) ore-figure helpers.
 *
 * ── Why this file exists ────────────────────────────────────────────────
 * Every "Ore" tonnage / block-count / grade figure in the SMIN UI MUST
 * come from `class_summary.by_kind.ore` on a block-model document — that
 * is the material-class rollup produced by `_auto_classify_and_persist`
 * on the backend after depth-overburden reassignment runs.
 *
 * A naive alternative source (`summary.ore_blocks`, `summary.ore_tonnage`,
 * `summary.avg_grade`) computes the count via `grade >= cutoff` BEFORE
 * material-class rules and depth-overburden reassignment run, so on real
 * deposits the two sources drift by ~5-10 %. Three separate SSoT bugs
 * have already been fixed in the wild by locking specific panels to the
 * `by_kind` source (mini-card, G-T readout, block-model summary panel).
 *
 * To stop this bug class recurring every time a new panel is written, ALL
 * ore-figure displays now import from this file and consume the shared
 * `readOreFromBm(bm)` helper or the ready-made <SsotOre…/> components.
 * Direct reads of `bm.summary.ore_*` or ad-hoc `bm.class_summary.by_kind.
 * ore.*` traversals are grep-forbidden going forward.
 *
 * ── Public API ──────────────────────────────────────────────────────────
 *   readOreFromBm(bm) → { blocks, tonnage, avgGrade, hasSsot, source }
 *   <SsotOreBlocks   bm={block_model} />  → number (or "—" if unavailable)
 *   <SsotOreTonnage  bm={block_model} unit="kt" />
 *   <SsotOreGrade    bm={block_model} decimals={4} />
 *
 * Fallback rule: if `class_summary.by_kind.ore` is missing (older block
 * models pre-auto-classify), we fall back to the naive `summary.*` values
 * BUT `source` is set to "legacy" so callers can render a subtle indicator
 * if desired.
 */

/**
 * Extract canonical ore figures from a block-model document.
 *
 * @param {object|null|undefined} bm A block-model document (may be null).
 * @returns {{
 *   blocks:   number|null,
 *   tonnage:  number|null,   // absolute tonnes (NOT kt)
 *   avgGrade: number|null,
 *   hasSsot:  boolean,       // true when reading from class_summary.by_kind
 *   source:   'ssot'|'legacy'|'none',
 * }}
 */
export function readOreFromBm(bm) {
  if (!bm) return { blocks: null, tonnage: null, avgGrade: null, hasSsot: false, source: "none" };

  const cs = bm.class_summary || null;
  const by = cs?.by_kind || null;
  // A rollup is only trustworthy when both `by_kind.ore` exists AND
  // `total_ore_tonnage` is populated (the auto-classify pipeline sets both
  // in the same transaction). Missing either flag means the classification
  // never ran — fall back to the naive summary.
  const hasSsot = !!(by && by.ore && cs.total_ore_tonnage != null);

  if (hasSsot) {
    return {
      blocks:   by.ore.blocks   ?? null,
      tonnage:  by.ore.tonnage  ?? null,
      avgGrade: by.ore.avg_grade ?? null,
      hasSsot:  true,
      source:   "ssot",
    };
  }

  const s = bm.summary || null;
  if (!s) return { blocks: null, tonnage: null, avgGrade: null, hasSsot: false, source: "none" };
  return {
    blocks:   s.ore_blocks   ?? null,
    tonnage:  s.ore_tonnage  ?? null,
    avgGrade: s.avg_grade    ?? null,
    hasSsot:  false,
    source:   "legacy",
  };
}

/**
 * Sibling reader for waste figures — same rules, same fallback ladder.
 * Kept alongside so panels showing an ore-vs-waste pair don't split
 * their reads across two files.
 */
export function readWasteFromBm(bm) {
  if (!bm) return { blocks: null, tonnage: null, hasSsot: false, source: "none" };
  const cs = bm.class_summary || null;
  const by = cs?.by_kind || null;
  const hasSsot = !!(by && by.waste && cs.total_ore_tonnage != null);
  if (hasSsot) {
    return {
      blocks:  by.waste.blocks  ?? null,
      tonnage: by.waste.tonnage ?? null,
      hasSsot: true,
      source:  "ssot",
    };
  }
  const s = bm.summary || null;
  if (!s) return { blocks: null, tonnage: null, hasSsot: false, source: "none" };
  return {
    blocks:  s.waste_blocks  ?? null,
    tonnage: s.waste_tonnage ?? null,
    hasSsot: false,
    source:  "legacy",
  };
}

const DASH = "—";

/**
 * Standalone string formatter (mirrors <OreTonnage/> logic) for panels that
 * need to inline the value inside a bigger string, chart label, or export
 * (i.e. anywhere JSX is inconvenient). Same units, same decimals, same
 * fallback. Prefer <OreTonnage/> when you're already rendering JSX.
 *
 * ── Unit contract (Iter 70 · Feb 2026) ──────────────────────────────────
 * Per platform-wide rebrand there is NO `kt` unit anywhere in the UI:
 *   • auto  → `X.XX Mt` when |value| ≥ 1_000_000, else `X.XX t`
 *             (2 decimals for both, `Bt` also supported for ≥ 1e9)
 *   • Mt    → `X.XX Mt`
 *   • t     → `X.XX t`
 * Callers passing the legacy `unit="kt"` will still get a 2-dec figure,
 * but with the `t` suffix (never `kt`) — kept for backwards compatibility
 * only, do not use in new code.
 */
export function formatTonnage(value, unit = "auto", { decimals = 2, fallback = DASH } = {}) {
  const num = Number(value);
  if (value == null || !Number.isFinite(num)) return fallback;
  const nf = (n, d = decimals) =>
    n.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
  if (unit === "auto") {
    const abs = Math.abs(num);
    if (abs >= 1e9)      return `${nf(num / 1e9)} Bt`;
    if (abs >= 1e6)      return `${nf(num / 1e6)} Mt`;
    return `${nf(num)} t`;
  }
  // Legacy `kt` and `Mt` explicit units — never emit "kt" as a label.
  const divisor = unit === "Mt" ? 1_000_000 : unit === "kt" ? 1_000 : 1;
  const suffix = unit === "Mt" ? "Mt" : "t"; // ← kt → t (Iter 70)
  return `${nf(num / divisor)} ${suffix}`;
}

/**
 * Reads the block model's ore rollup and mirrors the same source in
 * `data-ssot-source` so tests can assert which lookup path served the value.
 */
export function SsotOreBlocks({ bm, fallback = DASH, className = "", "data-testid": testId }) {
  const { blocks, source } = readOreFromBm(bm);
  const value = blocks == null ? fallback : blocks.toLocaleString();
  return (
    <span className={className} data-ssot-source={source} data-testid={testId}>{value}</span>
  );
}

/**
 * @param {'t'|'Mt'|'auto'} unit  Tonnage unit for display. `kt` accepted for
 * back-compat but always renders with the `t` suffix (Iter 70 · Feb 2026).
 */
export function SsotOreTonnage({ bm, unit = "auto", fallback = DASH, decimals = 2, className = "", "data-testid": testId }) {
  const { tonnage, source } = readOreFromBm(bm);
  const display = formatTonnage(tonnage, unit, { decimals, fallback });
  return (
    <span className={className} data-ssot-source={source} data-testid={testId}>{display}</span>
  );
}

/** Average grade of the ore-kind bucket (SSoT), NOT the whole block model. */
export function SsotOreGrade({ bm, fallback = DASH, decimals = 4, suffix = "", className = "", "data-testid": testId }) {
  const { avgGrade, source } = readOreFromBm(bm);
  const value = avgGrade == null ? fallback : `${avgGrade.toFixed(decimals)}${suffix}`;
  return (
    <span className={className} data-ssot-source={source} data-testid={testId}>{value}</span>
  );
}

/* ── Primitive display components ──────────────────────────────────────────
 * For panels whose ore figures come from OTHER SSoT-canonical collections
 * (resource_statements working set, portfolio rollup, density-class rollup,
 * class_summary preview endpoint) rather than a block-model document.
 * They receive the raw numeric value and centralise formatting only, so:
 *   1. Every "Ore" figure in the UI uses the same locale/decimals/fallback.
 *   2. A grep for `.ore_tonnage.toLocaleString` no longer returns dozens of
 *      ad-hoc formatters — new panels are steered to `<OreTonnage value=…/>`.
 *   3. The `data-ssot-source` attribute makes it obvious to reviewers /
 *      testing agents which lookup path served the value.
 * ─────────────────────────────────────────────────────────────────────── */

/** Count of ore blocks. `value` should already be an integer. */
export function OreBlocks({ value, fallback = DASH, source = "external", className = "", "data-testid": testId }) {
  const display = value == null ? fallback : Number(value).toLocaleString();
  return (
    <span className={className} data-ssot-source={source} data-testid={testId}>{display}</span>
  );
}

/**
 * Ore tonnage. `value` is expected in ABSOLUTE tonnes. Iter 70 · Feb 2026:
 * we no longer emit `kt` anywhere in the UI — `unit="auto"` picks between
 * `t` (<1 Mt) and `Mt` (≥ 1 Mt) with 2 decimals; `unit="Mt"` forces Mt.
 * The legacy `unit="kt"` value still divides by 1_000 but renders the `t`
 * suffix so old callers don't leak the deprecated label.
 */
export function OreTonnage({ value, unit = "auto", fallback = DASH, decimals = 2, source = "external", className = "", "data-testid": testId }) {
  const display = formatTonnage(value, unit, { decimals, fallback });
  return (
    <span className={className} data-ssot-source={source} data-testid={testId}>{display}</span>
  );
}

/** Average ore grade (%) — pass value in percentage points already. */
export function OreGrade({ value, fallback = DASH, decimals = 4, suffix = "", source = "external", className = "", "data-testid": testId }) {
  const display =
    value == null || !Number.isFinite(Number(value))
      ? fallback
      : `${Number(value).toFixed(decimals)}${suffix}`;
  return (
    <span className={className} data-ssot-source={source} data-testid={testId}>{display}</span>
  );
}
